'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Bot,
  Brain,
  Search,
  FileText,
  MessageSquare,
  BarChart,
  PieChart,
  TrendingUp,
  Target,
  Users,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Database,
  FileSearch as RAGFileSearch,
  Cpu,
  Zap,
  ArrowRight,
  ChevronRight,
  CheckCircle,
  Clock,
  LineChart,
  FileCheck,
  Sparkles,
  Award
} from 'lucide-react'
import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'

// Data from AI Agents page
const agents = [
  {
    title: 'Support Assistant',
    icon: MessageSquare,
    description: 'Get instant support and guidance for platform features via our chatbot.',
    capabilities: [
      '24/7 instant responses',
      'Contextual help',
      'Guided tutorials',
      'FAQ integration',
    ],
    isLive: true,
  },
  {
    title: 'Contract Search Agent',
    icon: Search,
    description: 'Find relevant contract opportunities using natural language processing.',
    capabilities: [
      'Natural language contract search',
      'Semantic matching of requirements',
      'Automated opportunity scoring',
      'Real-time alerts for matching contracts',
    ],
    isLive: true,
  },
  {
    title: 'Proposal Assistant',
    icon: FileText,
    description: 'Write and review proposals with higher win rates using AI guidance.',
    capabilities: [
      'Proposal template suggestions',
      'Compliance checking',
      'Win themes analysis',
      'Past performance optimization',
    ],
    isLive: true,
  },
  {
    title: 'AI Coach',
    icon: Sparkles,
    description: 'Transform your bidding strategy with AI-powered coaching that analyzes your documents, identifies improvement opportunities, and provides actionable recommendations.',
    capabilities: [
      'Document quality scoring',
      'Gap analysis & recommendations',
      'Strategic market positioning',
      'Competitive intelligence',
      'Progress tracking',
    ],
    isLive: true,
  },
]

// NEW: Visualization Examples Data
const visualizationExamples = [
  {
    title: 'Visualize Market Trends',
    icon: LineChart,
    description: 'Track market growth, identify cyclical patterns, and forecast future opportunities with dynamic line charts.',
  },
  {
    title: 'Benchmark Performance',
    icon: BarChart,
    description: 'Compare your win rates, proposal volume, and contract values against industry benchmarks using clear bar graphs.',
  },
  {
    title: 'Analyze Win/Loss Factors',
    icon: PieChart,
    description: 'Understand the key factors driving your successes and failures with insightful pie chart breakdowns and analysis.',
  },
  {
    title: 'Target High-Value Opportunities',
    icon: Target, 
    description: 'Focus your efforts on the most promising contracts with AI-driven opportunity scoring, clearly visualized for quick assessment.',
  },
]

// NEW: AI Proposal Generator Benefits Data
const proposalGeneratorBenefits = [
  {
    icon: Clock,
    title: "Accelerate Drafting",
    description: "Significantly reduce proposal writing time with AI-suggested content, templates, and automated formatting."
  },
  {
    icon: Sparkles,
    title: "Enhance Quality & Consistency",
    description: "Improve proposal clarity, tone, and consistency with AI-driven suggestions and best practice alignment."
  },
  {
    icon: FileCheck,
    title: "Ensure Compliance",
    description: "Automatically check proposals against solicitation requirements to minimize errors and ensure compliance."
  },
  {
    icon: Award,
    title: "Boost Win Rates",
    description: "Leverage insights from past performance and win themes to craft more compelling and competitive proposals."
  },
]

export default function AiAnalyticsPage() {
  return (
    <div className="bg-gradient-to-b from-background to-muted/30 min-h-screen dashboard-cards">
      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-primary/10 border border-primary/20 mb-8">
            <Bot className="h-12 w-12 lg:h-14 lg:w-14 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-foreground max-w-4xl mx-auto mb-6 leading-tight">
            Intelligent Contracting Suite
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Gain a decisive edge in government contracting with our integrated suite of AI-powered tools, advanced analytics, and streamlined proposal features.
          </p>
        </div>
      </section>

      <div className="container max-w-7xl mx-auto px-4 pb-16 lg:pb-24 space-y-20 lg:space-y-28">

        {/* AI Agents Section - Updated for Mixed Status */}
        <section>
          <h2 className="text-3xl font-bold text-center mb-4">AI-Powered Features</h2>
          <p className="text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Explore our live AI tools and get a sneak peek at powerful agents currently in development to further enhance your workflow.
          </p>
          <div className="grid gap-8 md:grid-cols-2">
            {agents.map((agent, index) => (
              <Card
                key={agent.title}
                className={`bg-card/80 backdrop-blur-sm h-full flex flex-col transition-all duration-300 hover:shadow-lg border shadow-md ${!agent.isLive ? 'opacity-75' : ''}`}
              >
                <CardHeader className="flex flex-row items-start gap-4 pb-4">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-primary/10 to-transparent flex-shrink-0">
                    <agent.icon className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                     <CardTitle className="text-xl font-semibold mb-1 flex items-center gap-2">
                       {agent.title}
                       {!agent.isLive && <Badge variant="outline" className="text-xs">Coming Soon</Badge>}
                     </CardTitle>
                    <p className="text-sm text-muted-foreground">{agent.description}</p>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow flex flex-col justify-end pt-0">
                  <div className={`mt-2 pt-3 border-t ${agent.isLive ? 'border-border' : 'border-dashed'}`}>
                     <h4 className="font-medium mb-2 text-xs text-muted-foreground">
                       {agent.isLive ? 'Key Capabilities:' : 'Planned Capabilities:'}
                     </h4>
                     <ul className="space-y-1.5">
                       {agent.capabilities.map((capability) => (
                         <li key={capability} className="flex items-center gap-2">
                           {agent.isLive ? (
                             <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                           ) : (
                             <Zap className="h-3.5 w-3.5 text-primary/50 flex-shrink-0" />
                           )}
                           <span className={`text-xs ${agent.isLive ? 'text-foreground/90' : 'text-muted-foreground'}`}>{capability}</span>
                         </li>
                       ))}
                     </ul>
                   </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Separator />

        {/* Advanced Analytics Section - Revamped with Visualizations */}
        <section>
          <h2 className="text-3xl font-bold text-center mb-4">Unlock Advanced Analytics</h2>
          <p className="text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Go beyond raw data. Visualize trends, benchmark performance, and gain actionable insights through intuitive charts and graphs.
          </p>
          {/* NEW: Visualization Examples Grid */}
          <div className="grid gap-8 md:grid-cols-2">
            {visualizationExamples.map((viz, index) => (
              <Card
                key={viz.title}
                className="p-6 bg-card/70 backdrop-blur-sm border rounded-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex flex-col items-center text-center"
              >
                <div className="p-4 rounded-full bg-gradient-to-br from-primary/10 to-transparent border mb-5">
                  <viz.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{viz.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-grow">{viz.description}</p>
              </Card>
            ))}
          </div>
        </section>

        <Separator />

        {/* NEW: AI Proposal Generator Section */}
        <section>
          <h2 className="text-3xl font-bold text-center mb-4">Streamline Proposals with AI</h2>
          <p className="text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Craft winning proposals faster and more effectively. Our AI Proposal Generator assists you from drafting to compliance checking.
          </p>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {proposalGeneratorBenefits.map((benefit, index) => (
              <Card 
                key={benefit.title} 
                className="flex flex-col items-center text-center p-6 bg-card/70 backdrop-blur-sm border rounded-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
              >
                <div className="p-4 rounded-full bg-gradient-to-br from-primary/10 to-transparent border mb-4 inline-flex">
                   <benefit.icon className="h-7 w-7 text-primary" />
                 </div>
                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
             <Button asChild size="lg">
               <Link href="/features" className="inline-flex items-center gap-2">
                 <Sparkles className="h-4 w-4" />
                 Explore Proposal Features
               </Link>
             </Button>
          </div>
        </section>

        <Separator />

        {/* Final CTA Section */}
        <section className="text-center bg-gradient-to-r from-sky-100 via-blue-100 to-indigo-100 dark:from-sky-900/30 dark:via-blue-900/30 dark:to-indigo-900/30 rounded-xl p-10 lg:p-16">
          <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-gray-100">Ready to Elevate Your Strategy?</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Explore plans, talk to our experts, or start your journey with GovCon Data today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="glow" className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-lg hover:shadow-xl transition-shadow">
              <Link href="/pricing">View Plans</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-background/50 backdrop-blur-sm">
              <Link href="/contact">Contact Sales</Link>
            </Button>
          </div>
        </section>

      </div>
    </div>
  )
} 